import { trigger } from '@angular/animations';
import { CdkMonitorFocus, FocusMonitor } from '@angular/cdk/a11y';
import { AsyncPipe, Location, NgOptimizedImage } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  effect,
  inject,
  Input,
  OnDestroy,
  OnInit,
  signal,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { CONTENT_LAYOUT_CONTAINER_ID, ImproverConstants, MAIN_MENU_CONTAINER_ID, NAVBAR_LOGO_CONTAINER_ID } from '@improver/app-config';
import { ButtonComponent } from '@improver/components/buttons';
import { ContextMenuDirective, ContextMenuItemComponent } from '@improver/components/context-menu';
import { TimeClockInOutTimerComponent } from '@improver/hours/time-clock-in-out-feature';
import { AuthDataLayerModule, InstanceLogoService, SecurityService } from '@improver/infrastructure/auth-data-layer';
import { InboxComponent } from '@improver/infrastructure/inbox-shared';
import { InstanceThemeDirective } from '@improver/infrastructure/layout-data-layer';
import { V4ComapanyEmployee, V4CompanyService } from '@improver/people/people-data-layer/v4-company';
import { CheckProductAvailabilityService } from '@improver/product/product-availability-data-layer';
import { MarketplaceProduct } from '@improver/product/product-data-layer';
import { GlobalSearchComponent } from '@improver/search/search-shared';
import { ImproverLayoutService } from '@improver/shared-ui/layout';
import { OffcanvasModule } from '@improver/shared-ui/offcanvas';
import { DesktopClassDirective, DesktopDirective, MobileClassDirective, MobileDirective } from '@improver/shared-ui/responsive';
import { TIME_AND_ATTENDANCE_PERMISSIONS } from '@improver/shared/permissions';
import { NavButtonComponent } from '@improver/shared/ui/components/nav-button';
import { TranslateModule } from '@ngx-translate/core';
import { Observable, of, SubscriptionLike } from 'rxjs';
import { distinctUntilChanged, filter, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { ImproverSidebarMobileComponent } from '../improver-sidebar';
import { GlobalActionsDropdownComponent } from './global-actions-dropdown/global-actions-dropdown.component';
import { HelpCenterDropdownComponent } from './help-center-dropdown/help-center-dropdown.component';
import { distractionFreeModeToggle } from './improver-navbar.animations';
import { MarketplaceNotificationComponent } from './marketplace-notification/marketplace-notification.component';
import { ProfileDropdownComponent } from './profile-dropdown/profile-dropdown.component';
import { SuiteDropdownComponent } from './suite-dropdown/suite-dropdown.component';
import { TimeConfigurationSetupsServiceAdapter } from '@improver/hours/time-settings-shared';

enum SkipToSubject {
  navbar,
  sideNavigation,
  content,
}

const focusContainersMap: Map<SkipToSubject, string> = new Map<SkipToSubject, string>([
  [SkipToSubject.navbar, NAVBAR_LOGO_CONTAINER_ID],
  [SkipToSubject.sideNavigation, MAIN_MENU_CONTAINER_ID],
  [SkipToSubject.content, CONTENT_LAYOUT_CONTAINER_ID],
]);

@Component({
  selector: 'lay-improver-navbar',
  templateUrl: './improver-navbar.component.html',
  styleUrls: ['./improver-navbar.component.scss'],
  encapsulation: ViewEncapsulation.None,
  animations: [trigger('distractionFreeModeToggle', distractionFreeModeToggle)],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AsyncPipe,
    AuthDataLayerModule,
    ButtonComponent,
    CdkMonitorFocus,
    ContextMenuDirective,
    ContextMenuItemComponent,
    DesktopClassDirective,
    DesktopDirective,
    GlobalActionsDropdownComponent,
    GlobalSearchComponent,
    HelpCenterDropdownComponent,
    ImproverSidebarMobileComponent,
    InboxComponent,
    InstanceThemeDirective,
    MarketplaceNotificationComponent,
    MobileClassDirective,
    MobileDirective,
    NavButtonComponent,
    NgOptimizedImage,
    OffcanvasModule,
    ProfileDropdownComponent,
    RouterLink,
    SuiteDropdownComponent,
    TimeClockInOutTimerComponent,
    TranslateModule,
  ],
})
export class ImproverNavbarComponent implements OnInit, OnDestroy {
  private readonly securityService = inject(SecurityService);
  private readonly productAvailabilityService: CheckProductAvailabilityService = inject(CheckProductAvailabilityService);
  private readonly router = inject(Router);
  private readonly v4CompanyService = inject(V4CompanyService);
  private readonly focusMonitor = inject(FocusMonitor);
  private readonly improverLayoutService = inject(ImproverLayoutService);
  private readonly instanceLogoService = inject(InstanceLogoService);
  private readonly location = inject(Location);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly timeConfigurationSetupsService = inject(TimeConfigurationSetupsServiceAdapter);
  @ViewChild(ContextMenuDirective) a11yContextMenu: ContextMenuDirective;
  @Input() logoLink = '/';
  @Input() offcanvasVisible = true;
  @Input() userActionsVisible = true;
  distractionFreeMode: boolean;
  hasPermissionToManageOwnHours = toSignal(this.securityService.hasPermissionStream(TIME_AND_ATTENDANCE_PERMISSIONS.MANAGE_OWN_HOURS));

  products$: Observable<MarketplaceProduct[]>;
  profileMenu = signal(true);

  readonly clockingProductAvailability$ = toObservable(this.profileMenu).pipe(
    switchMap((isProfileMenu) =>
      isProfileMenu
        ? of(false)
        : this.productAvailabilityService.checkSingleProductAvailability('clocking', ['TIME_AND_ATTENDANCE_MANAGE_CLOCKING'])
    ),
    shareReplay(1)
  );
  isClockingProductAvailable = toSignal(this.clockingProductAvailability$, { initialValue: false });

  private readonly loadConfigurationsEffect = effect(() => {
    const hasPermissions = this.securityService.hasAnyPermission(['MANAGE_OWN_HOURS', 'MANAGE_HOURS']);

    if (!this.profileMenu() && hasPermissions) {
      this.timeConfigurationSetupsService.getRegistrationOptions();
    }
  });

  readonly companyEmployees$: Observable<V4ComapanyEmployee[]> = this.v4CompanyService.getCompanyEmployees();
  readonly defaultLogoUrl: string = ImproverConstants.LOGO_URL;
  readonly logoPlaceholder: string = ImproverConstants.IMPROVER_4HUMAN_COMPANY_NAME;
  readonly logoContainerId: string = NAVBAR_LOGO_CONTAINER_ID;
  readonly skipToSubject: typeof SkipToSubject = SkipToSubject;
  readonly instanceLogo$: Observable<string> = this.instanceLogoService.getInstanceUrl();
  readonly instanceName$: Observable<string> = this.instanceLogoService.getInstanceName();
  private activePath: string = null;
  private a11yPanelShown: boolean = false;
  private locationSubscription: SubscriptionLike;

  ngOnInit() {
    this.resolveActiveLocationAndUserProfileVisibility();

    this.getDistractionFreeMode().subscribe((distractionFreeMode: boolean) => {
      this.distractionFreeMode = distractionFreeMode;

      this.changeDetectorRef?.markForCheck();
    });

    this.locationSubscription = this.location.subscribe((state: PopStateEvent) => {
      if (this.distractionFreeMode && state) {
        this.close(false);
      }
    });

    this.checkIfRouterChangedToProfile().subscribe();
  }

  ngOnDestroy() {
    if (this.locationSubscription) {
      this.locationSubscription.unsubscribe();
    }
  }

  close(resetLocation = true) {
    this.improverLayoutService.closeDistractionFreeMode();

    if (!resetLocation) {
      return;
    }

    this.activePath ? this.location.replaceState(`${this.activePath}`) : this.location.back();
  }

  a11yButtonFocusChange() {
    if (this.a11yPanelShown) {
      this.a11yPanelShown = false;

      return;
    }

    this.a11yContextMenu?.open(new Event('click'));
    this.a11yPanelShown = true;
  }

  skipTo(subject: SkipToSubject) {
    const element = document.getElementById(focusContainersMap.get(subject));
    const focusable = element.querySelector('[tabindex="0"]');

    if (focusable) {
      setTimeout(() => {
        this.focusMonitor.focusVia(focusable as HTMLElement, 'keyboard');
      });
    }
  }

  private isUserProfileDisplayed(): boolean {
    return this.activePath && this.checkIfURLMatchProfilesPath(this.activePath);
  }

  private checkIfURLMatchProfilesPath(url: string): boolean {
    return url === `/${ImproverConstants.ROUTES.PROFILE}` || url === `/${ImproverConstants.ROUTES.PROFILE_EDIT_EMAIL}`;
  }

  private getDistractionFreeMode(): Observable<boolean> {
    return this.improverLayoutService.getDistractionFreeMode().pipe(takeUntilDestroyed(this.destroyRef));
  }

  private resolveActiveLocationAndUserProfileVisibility() {
    this.activePath = this.location.path();
    this.profileMenu.set(this.isUserProfileDisplayed());
  }

  private checkIfRouterChangedToProfile(): Observable<boolean> {
    return this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map((event: NavigationEnd) => this.checkIfURLMatchProfilesPath(event?.url)),
      distinctUntilChanged(),
      tap((isUserProfile) => this.profileMenu.set(isUserProfile)),
      takeUntilDestroyed(this.destroyRef)
    );
  }
}
